import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { userAuth } from '../services/userAuth';
import { Button } from '../components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await userAuth.register(form);
      navigate('/account', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12 bg-gradient-to-b from-white to-slate-50" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-[#1a2e4e]" style={{ fontFamily: 'Chivo, sans-serif' }}>Create Account</h1>
          <p className="text-sm text-slate-500 mt-2">One account to reserve, view, and manage all your storage bookings.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-3" data-testid="register-form">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1.5">First Name</label>
              <input required value={form.first_name} onChange={update('first_name')} data-testid="register-first-name"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-[#1a2e4e]/20" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1.5">Last Name</label>
              <input required value={form.last_name} onChange={update('last_name')} data-testid="register-last-name"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-[#1a2e4e]/20" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1.5">Email</label>
            <input type="email" required value={form.email} onChange={update('email')} data-testid="register-email"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-[#1a2e4e]/20" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1.5">Phone</label>
            <input type="tel" required value={form.phone} onChange={update('phone')} data-testid="register-phone"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-[#1a2e4e]/20" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1.5">Password</label>
            <input type="password" required minLength={6} value={form.password} onChange={update('password')} data-testid="register-password"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-[#1a2e4e]/20" />
            <p className="text-xs text-slate-400 mt-1">At least 6 characters.</p>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white" data-testid="register-submit">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {loading ? 'Creating account…' : 'Create Account'}
          </Button>
          <p className="text-center text-sm text-slate-500 pt-2">
            Already have an account? <Link to="/login" className="text-[#1a2e4e] font-semibold hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
